// Fully client-side verification of a Peershare sealed dispute package (.zip).
// Nothing is uploaded — every check runs in the user's own browser.
//
// Trust chain reproduced here (mirrors backend custodian.py + routes.py):
//   1. zip_hash  == SHA-256( canonical_json(manifest.json) )
//      canonical_json == python json.dumps(obj, sort_keys=True, ensure_ascii=False)
//      (default separators ", " / ": ", keys sorted, non-ASCII left as-is).
//   2. ca_signature is a valid ed25519 signature of bytes.fromhex(zip_hash)
//      under ca_public_key  (backend sign() signs the RAW hash bytes).
//   3. For every bundled JSON file, SHA-256(file bytes) == manifest.file_digests[name]
//      → so no file inside the seal can be altered without breaking the CA signature.
//   4. evidence_events.json forms an unbroken hash chain (prev_hash links; genesis = 64 zeros).
//   5. Every evidence_files/* attachment hashes to its event.file.content_hash.
import JSZip from 'jszip';
import * as ed from '@noble/ed25519';

const GENESIS = '0'.repeat(64);
const enc = new TextEncoder();

// Matches python json.dumps(obj, sort_keys=True, ensure_ascii=False)
function canonical(v) {
  if (v === null) return 'null';
  if (Array.isArray(v)) return '[' + v.map(canonical).join(', ') + ']';
  if (typeof v === 'object') {
    return '{' + Object.keys(v).sort()
      .map((k) => JSON.stringify(k) + ': ' + canonical(v[k])).join(', ') + '}';
  }
  return JSON.stringify(v);
}

async function sha256hex(bytes) {
  const buf = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function verifyPackage(file) {
  const zip = await JSZip.loadAsync(file);
  const read = async (n) => {
    const f = zip.file(n);
    if (!f) throw new Error(`ไฟล์ "${n}" ไม่อยู่ในซอง — นี่อาจไม่ใช่ซองหลักฐานของ Peershare`);
    return f.async('string');
  };

  let manifest;
  let seal;
  try {
    manifest = JSON.parse(await read('manifest.json'));
    seal = JSON.parse(await read('seal.json'));
  } catch (e) {
    throw new Error('อ่าน manifest.json / seal.json ไม่สำเร็จ — ไฟล์อาจเสียหายหรือไม่ใช่ซองของ Peershare');
  }

  const checks = [];

  // 1) zip_hash binds the seal to the manifest content
  const computedZip = await sha256hex(enc.encode(canonical(manifest)));
  const zipOk = computedZip === seal.zip_hash;
  checks.push({
    key: 'zip', ok: zipOk,
    label: 'ลายนิ้วมือซอง (zip_hash) ตรงกับเนื้อหา manifest',
    detail: zipOk ? `0x${seal.zip_hash.slice(0, 10)}…` : 'ค่าที่คำนวณได้ไม่ตรงกับ seal',
  });

  // 2) CA ed25519 signature over the raw hash bytes
  let sigOk = false;
  try {
    sigOk = await ed.verifyAsync(
      ed.etc.hexToBytes(seal.ca_signature),
      ed.etc.hexToBytes(seal.zip_hash),
      ed.etc.hexToBytes(seal.ca_public_key),
    );
  } catch { sigOk = false; }
  checks.push({
    key: 'sig', ok: sigOk && zipOk,
    label: 'ลายเซ็นดิจิทัลของ Peershare (ed25519) ถูกต้อง',
    detail: sigOk ? 'ออกโดยกุญแจ CA ของ Peershare' : 'ลายเซ็นไม่ผ่านการตรวจสอบ',
  });

  // 3) each bundled file is covered by the signed manifest digest
  const fd = manifest.file_digests || {};
  const sub = [];
  let allDigest = Object.keys(fd).length > 0;
  for (const [name, dig] of Object.entries(fd)) {
    const f = zip.file(name);
    const ok = !!f && (await sha256hex(await f.async('uint8array'))) === dig;
    if (!ok) allDigest = false;
    sub.push({ name, ok });
  }
  checks.push({
    key: 'files', ok: allDigest, sub,
    label: 'ไฟล์ทุกชิ้นในซอง (สัญญา · สมาชิก · หลักฐาน) ไม่ถูกแก้ไข',
  });

  // 4) unbroken evidence hash chain
  let events = [];
  try { events = JSON.parse(await read('evidence_events.json')); } catch { events = []; }
  events = events.slice().sort((a, b) => a.seq - b.seq);
  let prev = GENESIS;
  let chainOk = events.length > 0;
  for (const e of events) { if (e.prev_hash !== prev) chainOk = false; prev = e.content_hash; }
  checks.push({
    key: 'chain', ok: chainOk,
    label: `สายโซ่หลักฐานต่อเนื่องครบถ้วน (${events.length} เหตุการณ์)`,
    detail: chainOk ? 'แต่ละเหตุการณ์ผูกกับเหตุการณ์ก่อนหน้า' : 'พบรอยขาดในสายโซ่',
  });

  // 5) attached evidence files hash to their recorded content_hash
  let fileChecked = 0;
  let fileHashOk = true;
  for (const e of events) {
    const fr = e.file;
    if (!fr || !fr.content_hash) continue;
    const match = Object.keys(zip.files).find((n) => n.startsWith(`evidence_files/${e.event_id}.`));
    if (match) {
      fileChecked++;
      const ok = (await sha256hex(await zip.file(match).async('uint8array'))) === fr.content_hash;
      if (!ok) fileHashOk = false;
    }
  }
  if (fileChecked > 0) {
    checks.push({
      key: 'evfiles', ok: fileHashOk,
      label: `ไฟล์แนบหลักฐาน (สลิป · รูป) ตรงกับลายนิ้วมือ (${fileChecked} ไฟล์)`,
    });
  }

  let contract = null;
  let members = [];
  try { contract = JSON.parse(await zip.file('contract.json').async('string')); } catch { /* optional */ }
  try { members = JSON.parse(await zip.file('members.json').async('string')); } catch { /* optional */ }

  return {
    verified: checks.every((c) => c.ok),
    checks,
    events,
    contract,
    members,
    meta: {
      dispute_id: manifest.dispute_id,
      circle_id: manifest.circle_id,
      requested_by: manifest.requested_by,
      subject: manifest.subject,
      reason: manifest.reason,
      issued_at: manifest.issued_at,
      zip_hash: seal.zip_hash,
      ca_public_key: seal.ca_public_key,
    },
  };
}
